"use client";

import { CLOUDINARY_CLOUD } from "@/lib/cloudinary";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Menu } from "lucide-react";

import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface HeroNavImageProps {
  title?: string;
  subtitle?: string;
  description?: string;
  poster?: string;
  /** Texto alternativo de `poster`. Vacío si la imagen es decorativa. */
  posterAlt?: string;
}

const HeroNavImage = ({ 
  title,
  subtitle,
  description,
  poster,
  posterAlt = "",
}: HeroNavImageProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationLinks = [
    { href: "/propiedades/venta", label: "Propiedades" },
    { href: "/propietarios", label: "Vende" },
    { href: "/personup", label: "PersonUp" },
    { href: "/universo-person", label: "Universo person" },
    { href: "/empleo", label: "Join us" },
    { href: "/blog", label: "Magazine" },
    { href: "/contacto", label: "Contacto" },
  ];

  return (
    <section className="font-poppins dark relative min-h-svh w-full overflow-hidden">
      {/* Background Image with Next.js Image optimization */}
      <Image
        src={poster || `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/c_scale,w_2000/q_auto/f_auto/person/horizontales/Person_2025_200_edit_q4au4b.jpg`}
        alt={posterAlt}
        fill
        priority
        quality={85}
        sizes="100vw"
        className="object-cover"
        referrerPolicy="no-referrer-when-downgrade"
      />
      
      {/* Dark overlay */}
      <div className="absolute left-0 top-0 h-full w-full bg-black/65 z-10" />

      <div className="relative z-20 flex h-full min-h-svh flex-col">
        {/* Navigation Header */}
        <div className="w-full flex items-center justify-between pt-4 md:pt-8 pb-4 px-4 sm:px-6 md:px-12 lg:px-16">
          {/* Left side: Logo, separator, and Navigation Menu */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Logo */}
            <Link href="/" className="relative h-10 w-28 md:h-14 md:w-40 flex-shrink-0">
              <Image
                src={`https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/c_scale,w_2000/q_auto/f_auto/logos/logonbflat_ybusfx.png`}
                alt="Company Logo"
                fill
                className="object-contain object-center"
                priority
              />
            </Link>

            {/* Separator - hidden on mobile */}
            <div className="h-8 w-px bg-white/30 hidden md:block" />

            {/* Navigation Menu - hidden on mobile */}
            <NavigationMenu className="hidden lg:block">
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/propiedades/venta"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    Propiedades
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/propietarios"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    Vende
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/personup"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    PersonUp
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/universo-person"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    Universo person
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/empleo"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    Join us
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/blog"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    Magazine
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/contacto"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    Contacto
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Right side: Mobile Menu & CTA */}
          <div className="flex items-center gap-4">
            {/* Desktop CTA - hidden on mobile */}
            <Link
              href="/valora-tu-vivienda"
              className="z-10 hidden lg:inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none whitespace-nowrap cursor-pointer"
            >
              Valora tu vivienda
            </Link>

            {/* Mobile Menu */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <button
                  className="text-white hover:text-white/80 transition-colors p-2 cursor-pointer"
                  aria-label="Open menu"
                >
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-[#193144] p-6 [&>button]:text-white [&>button]:hover:text-white/80">
                <SheetHeader className="pb-6">
                  <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                  <Link href="/" onClick={() => setIsMobileMenuOpen(false)} className="flex-shrink-0">
                    <Image
                      src={`https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/c_scale,w_2000/q_auto/f_auto/person/white_logo_transparent_cropped_btsmlv.png`}
                      alt="Person Logo"
                      width={96}
                      height={32}
                      className="object-contain object-left h-10 w-auto"
                    />
                  </Link>
                </SheetHeader>
                <nav className="flex flex-col gap-4">
                  {navigationLinks.map((link, index) => (
                    <Link
                      key={index}
                      href={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="text-white hover:text-white/80 transition-colors py-2 text-base"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Link
                    href="/valora-tu-vivienda"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-white hover:text-white/80 transition-colors py-2 text-base border-t border-white/20 pt-4 mt-2 cursor-pointer"
                  >
                    Valora tu vivienda
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Hero Content - centered on the page */}
        {(title || subtitle || description) && (
          <div className="flex-1 flex items-center justify-center pb-12 px-6 md:px-12 lg:px-16">
            <div className="flex w-full max-w-[75rem] mx-auto flex-col gap-2 text-center">
              {subtitle && (
                <p className="text-muted-foreground text-sm uppercase leading-none">
                  {subtitle}
                </p>
              )}
              {title && (
                <h1 className="leading-snug text-3xl md:text-4xl lg:text-3xl text-white">
                  {title}
                </h1>
              )}
              {description && (
                <p className="max-w-[20.25rem] mx-auto text-sm leading-relaxed text-white/90">
                  {description}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export { HeroNavImage };
